import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenderAPI } from '../utils/api';
import { Search, ChevronDown, ChevronUp, Loader2, Download, FileText, GitCompareArrows, X, Check } from 'lucide-react';

var STATUSES = ['new', 'reviewed', 'pursuing', 'preparing', 'submitted', 'won', 'lost', 'expired', 'not_relevant', 'duplicate'];

function TenderList() {
  var s1 = useState([]); var items = s1[0]; var setItems = s1[1];
  var s2 = useState(0); var total = s2[0]; var setTotal = s2[1];
  var s3 = useState(true); var loading = s3[0]; var setLoading = s3[1];
  var s4 = useState(''); var search = s4[0]; var setSearch = s4[1];
  var s5 = useState(''); var statusFilter = s5[0]; var setStatusFilter = s5[1];
  var s6 = useState(0); var minScore = s6[0]; var setMinScore = s6[1];
  var s7 = useState('created_date'); var sortBy = s7[0]; var setSortBy = s7[1];
  var s8 = useState('desc'); var sortDir = s8[0]; var setSortDir = s8[1];
  var s9 = useState(0); var page = s9[0]; var setPage = s9[1];
  var s10 = useState(false); var exporting = s10[0]; var setExporting = s10[1];
  var s11 = useState(false); var compareMode = s11[0]; var setCompareMode = s11[1];
  var s12 = useState([]); var selected = s12[0]; var setSelected = s12[1];
  var navigate = useNavigate();
  var limit = 25;

  var fetchTenders = useCallback(async function() {
    setLoading(true);
    try {
      var params = { skip: page * limit, limit: limit, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (minScore > 0) params.min_score = minScore;
      var res = await tenderAPI.list(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, sortBy, sortDir, search, statusFilter, minScore]);

  useEffect(function() { fetchTenders(); }, [fetchTenders]);

  var handleSort = function(col) {
    if (sortBy === col) { setSortDir(sortDir === 'desc' ? 'asc' : 'desc'); }
    else { setSortBy(col); setSortDir('desc'); }
    setPage(0);
  };

  var SortIcon = function(props) {
    if (sortBy !== props.col) return null;
    return sortDir === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />;
  };

  var getScoreBadge = function(score) {
    if (score >= 4) return 'bg-green-100 text-green-700';
    if (score >= 3) return 'bg-amber-100 text-amber-700';
    if (score >= 1) return 'bg-slate-100 text-slate-600';
    return 'bg-red-50 text-red-500';
  };

  var getStatusBadge = function(st) {
    var map = {
      new: 'bg-blue-100 text-blue-700', reviewed: 'bg-slate-100 text-slate-600',
      pursuing: 'bg-primary/10 text-primary', preparing: 'bg-indigo-100 text-indigo-700',
      submitted: 'bg-purple-100 text-purple-700', won: 'bg-green-100 text-green-700',
      lost: 'bg-red-100 text-red-600', expired: 'bg-slate-100 text-slate-400',
      not_relevant: 'bg-slate-50 text-slate-400', duplicate: 'bg-slate-50 text-slate-400',
    };
    return map[st] || 'bg-slate-100 text-slate-600';
  };

  var handleExport = async function(format) {
    setExporting(true);
    try {
      var params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (minScore > 0) params.min_score = minScore;
      var res = format === 'csv' ? await tenderAPI.exportCSV(params) : await tenderAPI.exportPDF(params);
      var blob = new Blob([res.data], { type: format === 'csv' ? 'text/csv' : 'application/pdf' });
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'tenders_export.' + format;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  var toggleSelect = function(id) {
    setSelected(function(prev) {
      if (prev.includes(id)) return prev.filter(function(x) { return x !== id; });
      if (prev.length >= 3) return prev;
      return prev.concat([id]);
    });
  };

  var handleCompare = function() {
    if (selected.length >= 2) {
      navigate('/tender-scout/compare?ids=' + selected.join(','));
    }
  };

  var statusOpts = [<option key="" value="">All Statuses</option>];
  for (var si = 0; si < STATUSES.length; si++) statusOpts.push(<option key={STATUSES[si]} value={STATUSES[si]}>{STATUSES[si]}</option>);

  var scoreOpts = [];
  for (var sc = 0; sc <= 5; sc++) scoreOpts.push(<option key={sc} value={sc}>{sc === 0 ? 'Any Score' : sc + '+'}</option>);

  var rows = [];
  for (var i = 0; i < items.length; i++) {
    var t = items[i];
    var closingStr = t.closing_date ? new Date(t.closing_date).toLocaleDateString() : '-';
    var valStr = t.estimated_value_myr ? 'RM ' + Number(t.estimated_value_myr).toLocaleString() : '-';
    var isSelected = selected.includes(t._id);
    rows.push(
      <tr key={t._id} className={'border-b border-slate-100 hover:bg-slate-50 cursor-pointer ' + (isSelected ? 'bg-primary/5' : '')} data-testid={'tender-row-' + t._id}>
        {compareMode && (
          <td className="py-3 px-2" onClick={function(id) { return function(e) { e.stopPropagation(); toggleSelect(id); }; }(t._id)}>
            <div className={'w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ' + (isSelected ? 'bg-primary border-primary' : 'border-slate-300')}>
              {isSelected && <Check size={12} className="text-white" />}
            </div>
          </td>
        )}
        <td className="py-3 px-4" onClick={function(id) { return function() { navigate('/tender-scout/tenders/' + id); }; }(t._id)}><span className={'text-xs font-bold px-2 py-1 rounded ' + getScoreBadge(t.final_score)}>{t.final_score}</span></td>
        <td className="py-3 px-4" onClick={function(id) { return function() { navigate('/tender-scout/tenders/' + id); }; }(t._id)}><div className="max-w-md"><p className="text-sm font-medium text-slate-900 truncate">{t.title}</p><p className="text-xs text-slate-400">{t.reference}</p></div></td>
        <td className="py-3 px-4 text-sm text-slate-600" onClick={function(id) { return function() { navigate('/tender-scout/tenders/' + id); }; }(t._id)}>{t.organisation || '-'}</td>
        <td className="py-3 px-4 text-sm text-slate-600" onClick={function(id) { return function() { navigate('/tender-scout/tenders/' + id); }; }(t._id)}>{closingStr}</td>
        <td className="py-3 px-4 text-sm text-slate-600" onClick={function(id) { return function() { navigate('/tender-scout/tenders/' + id); }; }(t._id)}>{valStr}</td>
        <td className="py-3 px-4" onClick={function(id) { return function() { navigate('/tender-scout/tenders/' + id); }; }(t._id)}><span className={'text-xs px-2 py-1 rounded ' + getStatusBadge(t.status)}>{t.status}</span></td>
        <td className="py-3 px-4 text-xs text-slate-400" onClick={function(id) { return function() { navigate('/tender-scout/tenders/' + id); }; }(t._id)}>{t.source_name}</td>
      </tr>
    );
  }

  var totalPages = Math.ceil(total / limit);

  return (
    <div className="p-3 sm:p-6 space-y-4" data-testid="tender-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div><h1 className="text-xl sm:text-2xl font-bold text-slate-900">All Tenders</h1><p className="text-sm text-slate-500">{total} tenders found</p></div>
        <div className="flex gap-2 flex-wrap">
          {compareMode ? (
            <>
              <span className="text-sm text-slate-500 self-center">{selected.length}/3 selected</span>
              <button onClick={handleCompare} disabled={selected.length < 2} className="px-3 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50 flex items-center gap-1.5" data-testid="compare-go-btn">
                <GitCompareArrows size={14} /> Compare
              </button>
              <button onClick={function() { setCompareMode(false); setSelected([]); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50" data-testid="compare-cancel-btn">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={function() { setCompareMode(true); setSelected([]); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1.5" data-testid="compare-mode-btn">
                <GitCompareArrows size={14} /> Compare
              </button>
              <button onClick={function() { handleExport('csv'); }} disabled={exporting} className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50" data-testid="export-csv-btn">
                <Download size={14} /> CSV
              </button>
              <button onClick={function() { handleExport('pdf'); }} disabled={exporting} className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50" data-testid="export-pdf-btn">
                <FileText size={14} /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={function(e) { setSearch(e.target.value); setPage(0); }} placeholder="Search tenders..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tender-search-input" />
        </div>
        <select value={statusFilter} onChange={function(e) { setStatusFilter(e.target.value); setPage(0); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tender-status-filter">{statusOpts}</select>
        <select value={minScore} onChange={function(e) { setMinScore(parseInt(e.target.value)); setPage(0); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tender-score-filter">{scoreOpts}</select>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {compareMode && <th className="py-3 px-2 w-8"></th>}
                <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 cursor-pointer select-none" onClick={function() { handleSort('final_score'); }}>Score <SortIcon col="final_score" /></th>
                <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 cursor-pointer select-none" onClick={function() { handleSort('title'); }}>Title <SortIcon col="title" /></th>
                <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 cursor-pointer select-none" onClick={function() { handleSort('organisation'); }}>Organisation <SortIcon col="organisation" /></th>
                <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 cursor-pointer select-none" onClick={function() { handleSort('closing_date'); }}>Closing <SortIcon col="closing_date" /></th>
                <th className="py-3 px-4 text-left text-xs font-medium text-slate-500">Value</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 cursor-pointer select-none" onClick={function() { handleSort('status'); }}>Status <SortIcon col="status" /></th>
                <th className="py-3 px-4 text-left text-xs font-medium text-slate-500">Source</th>
              </tr>
            </thead>
            <tbody>{rows.length > 0 ? rows : <tr><td colSpan={compareMode ? 8 : 7} className="py-12 text-center text-sm text-slate-400">No tenders found. Run scraping from the dashboard to discover tenders.</td></tr>}</tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Page {page + 1} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={function() { setPage(Math.max(0, page - 1)); }} disabled={page === 0} className="px-3 py-1 border border-slate-200 rounded text-sm disabled:opacity-50">Prev</button>
            <button onClick={function() { setPage(Math.min(totalPages - 1, page + 1)); }} disabled={page >= totalPages - 1} className="px-3 py-1 border border-slate-200 rounded text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenderList;
